import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  SparklesIcon,
  ChartBarIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useSuccessionTheme } from '../SuccessionThemeProvider';

const ValuationAgentMiniWidget = ({
  user,
  currentUser,
  company,
  features,
  onExpand,
  onQuickAction
}) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [topRecommendation, setTopRecommendation] = useState(null);
  const [confidenceScore, setConfidenceScore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if valuation agent is enabled
  const isEnabled = Array.isArray(features) &&
                   features.includes('valuation_agent') &&
                   features.includes('agent_widgets');

  // Quick analysis to get top recommendation and confidence
  useEffect(() => {
    if (!isEnabled) return;

    const analyzeQuickRecommendation = async () => {
      try {
        setIsLoading(true);

        // Use same logic as the main widget but simplified for top recommendation only
        const analysis = await analyzeCurrentValuation(user);
        setTopRecommendation(analysis.top_recommendation);
        setConfidenceScore(analysis.confidence_score);
      } catch (error) {
        console.error('Error loading mini widget analysis:', error);
        // Fallback recommendation
        setTopRecommendation({
          title: 'Improve Revenue Data',
          description: 'Add revenue breakdown for better accuracy',
          urgency: 'high',
          quickActions: ['Add Revenue Data']
        });
        setConfidenceScore({ score: 45, level: 'Low' });
      } finally {
        setIsLoading(false);
      }
    };

    analyzeQuickRecommendation();
  }, [user, isEnabled]);

  // Simplified analysis function focused on top priority
  const analyzeCurrentValuation = async (userData) => {
    // Check company capabilities through features array
    const safeFeatures = Array.isArray(features) ? features : [];
    const allowsHistoricalData = true;
    const allowsGrowthRates = !safeFeatures.includes('allow_user_to_specify_revenue_growth_rate'); // When true, this flag HIDES growth rate input
    const allowsExpenseInput = true;

    // Check for key data points
    const hasRevenue = (userData?.fee_based_revenue > 0) ||
                      (userData?.trailer_revenue > 0) ||
                      (userData?.transactional_revenue > 0) ||
                      (userData?.direct_revenue > 0);
    const hasExpenses = userData?.gross_expenses > 0;
    const hasHistoricalData = userData?.year_1_gross > 0 || userData?.year_2_gross > 0;
    const hasGrowthRates = userData?.revenue_growth_rate > 0 || userData?.expense_growth_rate > 0;

    // Calculate total revenue
    const totalRevenue = (userData?.fee_based_revenue || 0) +
                        (userData?.trailer_revenue || 0) +
                        (userData?.transactional_revenue || 0) +
                        (userData?.direct_revenue || 0);

    // Determine top priority recommendation based on missing critical data
    let topRec = null;
    let urgency = 'medium';
    let quickActions = [];

    if (!hasRevenue) {
      topRec = {
        title: 'Add Revenue Breakdown',
        description: 'Revenue data is essential for valuation accuracy',
        urgency: 'high',
        quickActions: ['Enter Revenue Data', 'Quick Setup'],
        impact: '+25 points'
      };
    } else if (allowsHistoricalData && !hasHistoricalData) {
      topRec = {
        title: 'Add Historical Data',
        description: 'Historical trends boost buyer confidence',
        urgency: 'high',
        quickActions: ['Add 2-3 Years', 'Import Data'],
        impact: '+15 points'
      };
    } else if (allowsExpenseInput && !hasExpenses) {
      topRec = {
        title: 'Complete Expense Data',
        description: 'Expense data needed for profitability analysis',
        urgency: 'medium',
        quickActions: ['Add Expenses', 'Calculate EBITDA'],
        impact: '+12 points'
      };
    } else if (allowsGrowthRates && !hasGrowthRates) {
      topRec = {
        title: 'Set Growth Assumptions',
        description: 'Growth projections improve valuation modeling',
        urgency: 'medium',
        quickActions: ['Add Growth Rate', 'Use Conservative 5%'],
        impact: '+8 points'
      };
    } else {
      // All basic data present, suggest optimization
      topRec = {
        title: 'Optimize Valuation',
        description: 'Fine-tune your profile for maximum value',
        urgency: 'low',
        quickActions: ['Review Details', 'Professional Review'],
        impact: '+5 points'
      };
    }

    // Calculate simplified confidence score
    let score = 40; // Base score
    if (hasRevenue) score += 25;
    if (hasHistoricalData) score += 20;
    if (hasExpenses) score += 15;
    if (hasGrowthRates) score += 10;

    const level = score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Low';

    return {
      top_recommendation: topRec,
      confidence_score: { score: Math.min(score, 100), level }
    };
  };

  const handleQuickAction = (action) => {
    if (onQuickAction) {
      onQuickAction(action, topRecommendation);
    }
  };

  const handleExpand = () => {
    if (onExpand) {
      onExpand();
    }
  };

  // Don't render if not enabled
  if (!isEnabled) return null;

  // Get confidence styling
  const getConfidenceStyle = (level) => {
    switch (level) {
      case 'High':
        return 'text-succession-success bg-succession-success-10';
      case 'Medium':
        return 'text-succession-warning bg-succession-warning-10';
      default:
        return 'text-succession-error bg-succession-error-10';
    }
  };

  const getUrgencyStyle = (urgency) => {
    switch (urgency) {
      case 'high':
        return 'border-l-4 border-succession-error';
      case 'medium':
        return 'border-l-4 border-succession-warning';
      default:
        return 'border-l-4 border-succession-primary';
    }
  };

  return (
    <div className="bg-succession-bg-primary shadow-succession-sm rounded-lg border border-succession-border p-4 transition-all duration-300 hover:shadow-succession-md">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <SparklesIcon className="h-4 w-4 text-succession-primary" />
          <h3 className="text-sm font-medium text-succession-text-primary">
            {intl.formatMessage({ id: 'succession.agent_system.valuation_agent_mini_widget.title' })}
          </h3>
        </div>

        {confidenceScore && (
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceStyle(confidenceScore.level)}`}>
            {confidenceScore.score}% {confidenceScore.level}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="animate-pulse">
          <div className="h-3 bg-succession-bg-tertiary rounded w-3/4 mb-2"></div>
          <div className="h-2 bg-succession-bg-tertiary rounded w-1/2"></div>
        </div>
      ) : (
        <>
          {/* Top Recommendation */}
          {topRecommendation && (
            <div className={`mb-3 p-3 bg-succession-bg-secondary rounded-md ${getUrgencyStyle(topRecommendation.urgency)}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-succession-text-primary mb-1">
                    🎯 {topRecommendation.title}
                  </p>
                  <p className="text-xs text-succession-text-secondary">
                    {topRecommendation.description}
                  </p>
                  {topRecommendation.impact && (
                    <p className="text-xs text-succession-success font-medium mt-1">
                      📈 {topRecommendation.impact} {intl.formatMessage({ id: 'succession.agent_system.valuation_agent_mini_widget.confidence_boost' })}
                    </p>
                  )}
                </div>
              </div>

              {/* Quick Action Buttons */}
              {topRecommendation.quickActions && topRecommendation.quickActions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {topRecommendation.quickActions.slice(0, 2).map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickAction(action)}
                      className="px-2 py-1 bg-succession-primary text-white text-xs rounded hover:bg-succession-primary-hover transition-colors"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Progress Bar */}
          {confidenceScore && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-succession-text-secondary mb-1">
                <span>{intl.formatMessage({ id: 'succession.agent_system.valuation_agent_mini_widget.overall_health' })}</span>
                <span>{confidenceScore.score}%</span>
              </div>
              <div className="w-full bg-succession-bg-tertiary rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    confidenceScore.level === 'High' ? 'bg-succession-success' :
                    confidenceScore.level === 'Medium' ? 'bg-succession-warning' :
                    'bg-succession-error'
                  }`}
                  style={{ width: `${Math.min(confidenceScore.score, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Expand Button */}
          <button
            onClick={handleExpand}
            className="w-full flex items-center justify-center px-3 py-2 text-xs font-medium text-succession-primary hover:text-succession-primary-hover border border-succession-border rounded-md hover:bg-succession-primary-10 transition-colors"
          >
            {intl.formatMessage({ id: 'succession.agent_system.valuation_agent_mini_widget.view_full_analysis' })}
            <ArrowRightIcon className="h-3 w-3 ml-1" />
          </button>
        </>
      )}
    </div>
  );
};

export default ValuationAgentMiniWidget;